export const INSULATION_FACTORS = Object.freeze({
  'very-good': Object.freeze({ lower: 12, upper: 18 }),
  good: Object.freeze({ lower: 18, upper: 27 }),
  average: Object.freeze({ lower: 27, upper: 40 }),
  weak: Object.freeze({ lower: 40, upper: 50 }),
  unsanitized: Object.freeze({ lower: 50, upper: 65 }),
});

export const roundToHalfKilowatt = (value) => Math.round(value * 2) / 2;

export function calculateHeatingOutput({ area, height, insulation }) {
  const parsedArea = Number(area);
  const parsedHeight = Number(height);
  const factors = INSULATION_FACTORS[insulation];

  if (!Number.isFinite(parsedArea) || parsedArea <= 0) throw new RangeError('area');
  if (!Number.isFinite(parsedHeight) || parsedHeight <= 0) throw new RangeError('height');
  if (!factors) throw new RangeError('insulation');

  const volume = parsedArea * parsedHeight;
  const rawLower = volume * factors.lower / 1000;
  const rawUpper = volume * factors.upper / 1000;

  return {
    area: parsedArea,
    height: parsedHeight,
    insulation,
    volume,
    factorLower: factors.lower,
    factorUpper: factors.upper,
    lower: roundToHalfKilowatt(rawLower),
    upper: roundToHalfKilowatt(rawUpper),
    rawLower,
    rawUpper,
    isVeryLow: rawUpper < 4,
    isUnusuallyHigh: rawUpper > 12,
    isPassiveHouseCase: insulation === 'very-good',
  };
}

const formatOneDecimal = (value) => new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
}).format(value);

const formatArea = (value) => new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 2,
}).format(value);

const formatHeight = (value) => new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(value);

const INSULATION_SUMMARIES = Object.freeze({
  'very-good': 'sehr gute Dämmung',
  good: 'gute Dämmung',
  average: 'durchschnittliche Dämmung',
  weak: 'schwache Dämmung',
  unsanitized: 'unsanierten Zustand mit hohen Wärmeverlusten',
});

export function buildProductFilterUrl(baseUrl, lower, upper) {
  const url = new URL(baseUrl, 'https://ofenerlebnis.de');
  url.searchParams.set('oe_power_verified', '1');
  url.searchParams.set('oe_power_min', String(lower));
  url.searchParams.set('oe_power_max', String(upper));
  url.hash = `oe?${new URLSearchParams({
    'min.nennwaermeleistung': String(lower),
    'max.nennwaermeleistung': String(upper),
  })}`;
  return `${url.pathname}${url.search}${url.hash}`;
}

export function getSpecialNotice(calculation) {
  if (calculation.isUnusuallyHigh) {
    return 'Hinweis: Der ermittelte Leistungsbedarf ist ungewöhnlich hoch. Wählen Sie nicht allein deshalb einen sehr leistungsstarken Kaminofen, sondern lassen Sie Gebäude, Wärmeverteilung und Aufstellsituation individuell prüfen.';
  }
  if (calculation.isVeryLow) {
    const passiveAddition = calculation.isPassiveHouseCase
      ? ' Passiv- und Niedrigenergiehäuser sollten zusätzlich individuell geprüft werden.'
      : '';
    return `Hinweis: Der ermittelte Leistungsbedarf ist sehr niedrig. Achten Sie besonders auf Kaminöfen mit kleiner Nennwärmeleistung, damit der Raum nicht unnötig überheizt wird.${passiveAddition}`;
  }
  if (calculation.isPassiveHouseCase) {
    return 'Hinweis: Passiv- und Niedrigenergiehäuser haben einen außergewöhnlich niedrigen Wärmebedarf und sollten individuell geprüft werden.';
  }
  return '';
}

const HTMLElementBase = globalThis.HTMLElement ?? class {};

class HeatingOutputCalculator extends HTMLElementBase {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.form = this.querySelector('[data-calculator-form]');
    this.error = this.querySelector('[data-calculator-error]');
    this.status = this.querySelector('[data-calculator-status]');
    this.result = this.querySelector('[data-calculator-result]');
    this.resultPlaceholder = this.querySelector('[data-result-placeholder]');
    this.resultContent = this.querySelector('[data-result-content]');
    this.productCta = this.querySelector('[data-product-cta]');

    this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  handleSubmit(event) {
    event.preventDefault();
    this.clearError();

    if (!this.form.reportValidity()) return;
    const formData = new FormData(this.form);

    try {
      const calculation = calculateHeatingOutput({
        area: formData.get('area'),
        height: formData.get('height'),
        insulation: formData.get('insulation'),
      });
      this.renderResult(calculation);
    } catch (error) {
      this.showError('Bitte prüfen Sie Fläche, Raumhöhe und Dämmzustand.');
    }
  }

  renderResult(calculation) {
    const range = `${formatOneDecimal(calculation.lower)}–${formatOneDecimal(calculation.upper)}`;
    this.querySelector('[data-result-title]').textContent = 'Empfohlener Leistungsbereich';
    this.querySelector('[data-result-range]').textContent = range;
    this.querySelector('[data-result-summary]').textContent = `Für ${formatArea(calculation.area)} m², ${formatHeight(calculation.height)} m Raumhöhe und ${INSULATION_SUMMARIES[calculation.insulation]}.`;

    this.result.dataset.powerMin = String(calculation.lower);
    this.result.dataset.powerMax = String(calculation.upper);
    this.result.dataset.powerUnit = 'kW';
    this.result.dataset.productMatching = 'prepared';

    this.productCta.href = buildProductFilterUrl(
      this.productCta.dataset.baseUrl,
      calculation.lower,
      calculation.upper,
    );
    this.productCta.dataset.filterState = 'verified-power-range';

    const notices = this.querySelector('[data-result-notices]');
    notices.replaceChildren();
    const specialNotice = getSpecialNotice(calculation);
    if (specialNotice) notices.append(this.createNotice(specialNotice));

    this.resultPlaceholder.hidden = true;
    this.resultContent.hidden = false;
    this.status.textContent = `Orientierungswert berechnet: ${range} Kilowatt.`;
    this.resultContent.focus({ preventScroll: true });
    this.result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  createNotice(text) {
    const paragraph = document.createElement('p');
    paragraph.className = 'oe-heating-output-calculator__notice';
    paragraph.textContent = text;
    return paragraph;
  }

  showError(message) {
    this.error.textContent = message;
    this.error.hidden = false;
    this.error.focus?.();
  }

  clearError() {
    this.error.textContent = '';
    this.error.hidden = true;
  }
}

if (globalThis.customElements && !globalThis.customElements.get('oe-heating-output-calculator')) {
  globalThis.customElements.define('oe-heating-output-calculator', HeatingOutputCalculator);
}
